/**
 * OneCalla Intake Form - Main App
 *
 * Wraps the form in required providers:
 * - GoogleReCaptchaProvider (reCAPTCHA v3, loaded dynamically)
 * - React Hook Form's FormProvider (shared form state across steps)
 * - Supabase Auth session listener
 *
 * Handles:
 * - Multi-step form navigation
 * - Draft persistence (save on step transition, restore on load)
 * - Session timeout (15 min inactivity)
 * - Form submission via submitForm()
 * - Analytics initialization
 */

import { useCallback, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { fullFormSchema, type FullFormData } from "@/schemas/formSchemas";
import { useDraftPersistence } from "@/hooks/useDraftPersistence";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { useAnalytics } from "@/hooks/useAnalytics";
import { initAnalytics, identifyUser } from "@/lib/analytics";
import { submitForm } from "@/lib/submitForm";
import { supabase } from "@/lib/supabase";
import { captureReferrerCode } from "@/lib/referral";

import FormLayout from "@/components/layout/FormLayout";
import Step1Policy from "@/components/steps/Step1Policy";
import Step2AboutYou from "@/components/steps/Step2AboutYou";
import Step3Vehicle from "@/components/steps/Step3Vehicle";
import Step4Details from "@/components/steps/Step4Details";
import Step5CoInsured from "@/components/steps/Step5CoInsured";
import Step6Optional from "@/components/steps/Step6Optional";
import AdminView from "@/components/admin/AdminView";
import ClaimPage from "@/components/ClaimPage";
import { SuccessScreen } from "@/components/SuccessScreen";
import type { UseFormSetValue, FieldPath } from "react-hook-form";
import { LandingChoice } from "@/components/upload/LandingChoice";
import { DecPageUploader } from "@/components/upload/DecPageUploader";
import { ExtractionReview } from "@/components/upload/ExtractionReview";
import { ExtractionMetadataContext } from "@/hooks/useExtractionMetadata";
import type { ExtractionResponse, FieldMetadata } from "@/types/extraction";
import type { MappedFormData } from "@/lib/decPageMapping";

const TOTAL_STEPS = 6;
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";

type IntakePhase = "choosing" | "uploading" | "reviewing" | "form";

function IntakeForm() {
  const [intakePhase, setIntakePhase] = useState<IntakePhase>("choosing");
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [extractionMetadata, setExtractionMetadata] = useState<FieldMetadata>({});
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastSubmissionId, setLastSubmissionId] = useState<string | null>(null);
  const [lastEmailStatus, setLastEmailStatus] = useState<"sent" | "failed" | undefined>(undefined);

  const { executeRecaptcha } = useGoogleReCaptcha();
  const { saveDraft, loadDraft, lastSaved, isSaving } = useDraftPersistence();
  const { trackEvent } = useAnalytics();

  const methods = useForm<FullFormData>({
    resolver: zodResolver(fullFormSchema),
    mode: "onBlur",
    defaultValues: {
      step2: { homeOwnership: undefined },
      step3: { primaryVehicleUse: undefined, commuteMileage: undefined },
      step4: { educationLevel: undefined, yearFirstLicensed: undefined, studentDegreePursuing: "" },
      step5: { hasCoInsured: false, drivers: [] },
      step6: { hasCompetitiveQuote: false, useSigninEmail: false },
      honeypot: { website: "" },
    },
  });

  // Session timeout: clear form after 15 min of inactivity
  const handleTimeout = useCallback(() => {
    methods.reset();
    setCurrentStep(1);
    setCompletedSteps([]);
    // TODO: Show a modal explaining the timeout
  }, [methods]);

  const { showWarning: showTimeoutWarning } = useSessionTimeout(handleTimeout);

  // Restore draft on initial load, or pre-fill from audit URL params
  useEffect(() => {
    // Always capture the referral code first, even when a draft exists --
    // a returning user may have arrived through a different share link.
    captureReferrerCode();

    async function restore() {
      const draft = await loadDraft();
      if (draft?.form_data) {
        methods.reset(draft.form_data as FullFormData);
        setCurrentStep(draft.current_step || 1);
        setIntakePhase("form");
        // Restore extraction context so PreFilledBadges and the submit
        // payload continue to reflect the uploaded policy.
        if (draft.extraction_context) {
          const ctx = draft.extraction_context as {
            storagePath: string;
            extractedPolicy: ExtractionResponse["extracted"];
            metadata: FieldMetadata;
          };
          setExtraction({
            ok: true,
            extracted: ctx.extractedPolicy,
            storagePath: ctx.storagePath,
            durationMs: 0,
          });
          setExtractionMetadata(ctx.metadata);
        }
        trackEvent("draft_resumed", { step: draft.current_step });
      } else {
        // Check for pre-fill from audit funnel
        const params = new URLSearchParams(window.location.search);
        if (params.get("ref") === "audit") {
          const carrier = params.get("carrier");
          const vehicle = params.get("vehicle");
          const zip = params.get("zip");
          if (carrier) methods.setValue("step1.carrierName", carrier);
          if (zip) methods.setValue("step2.mailingAddressZip", zip);
          if (vehicle) {
            const parts = vehicle.split(" ");
            if (parts[0]) methods.setValue("step3.vehicleYear", parseInt(parts[0]) || 0);
            if (parts[1]) methods.setValue("step3.vehicleMake", parts[1]);
            if (parts.slice(2).join(" ")) methods.setValue("step3.vehicleModel", parts.slice(2).join(" "));
          }
          // Audit-funnel users skip the landing choice and go straight to the form
          setIntakePhase("form");
        }
        trackEvent("form_started", { ref: params.get("ref") || "direct" });
      }
    }
    restore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Navigate to next step
  const handleNext = async () => {
    // Validate current step fields before advancing
    const stepFieldMap: Record<number, string> = {
      1: "step1",
      2: "step2",
      3: "step3",
      4: "step4",
      5: "step5",
      6: "step6",
    };

    const stepKey = stepFieldMap[currentStep];
    const isValid = await methods.trigger(stepKey as keyof FullFormData);

    if (!isValid) {
      // Scroll to first error
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    // Save draft on step transition
    const draftExtractionContext = extraction
      ? {
          storagePath: extraction.storagePath,
          extractedPolicy: extraction.extracted,
          metadata: extractionMetadata,
        }
      : undefined;
    await saveDraft(currentStep, methods.getValues(), draftExtractionContext);
    trackEvent("step_completed", { step: currentStep });

    if (currentStep < TOTAL_STEPS) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
      setCurrentStep((prev) => prev + 1);
    }
  };

  // Navigate to previous step
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  // Jump to a specific step (backward only)
  const handleStepClick = (step: number) => {
    if (step <= currentStep && step >= 1) {
      setCurrentStep(step);
    }
  };

  // Submit the form
  const handleSubmit = async () => {
    // Validate step 6 fields + consent
    const [step6Valid, consentValid] = await Promise.all([
      methods.trigger("step6"),
      methods.trigger("consent"),
    ]);

    if (!step6Valid || !consentValid) {
      const firstError = document.querySelector('[data-error="true"]');
      if (firstError) {
        firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    const formData = methods.getValues();
    const extractionContext = extraction
      ? {
          storagePath: extraction.storagePath,
          extractedPolicy: extraction.extracted,
          metadata: extractionMetadata,
        }
      : undefined;
    const result = await submitForm(formData, executeRecaptcha, extractionContext);

    if (result.success) {
      setSubmitSuccess(true);
      setLastSubmissionId(result.submissionId ?? null);
      setLastEmailStatus(result.userEmailStatus);
      setCompletedSteps([1, 2, 3, 4, 5, 6]);
    } else {
      setSubmitError(result.error || "Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  // Render current step
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Policy />;
      case 2:
        return <Step2AboutYou />;
      case 3:
        return <Step3Vehicle />;
      case 4:
        return <Step4Details />;
      case 5:
        return <Step5CoInsured />;
      case 6:
        return <Step6Optional />;
      default:
        return null;
    }
  };

  if (submitSuccess) {
    const formData = methods.getValues();
    return (
      <SuccessScreen
        carrierName={formData.step1.carrierName === "Other" ? (formData.step1.carrierNameOther || "Your carrier") : formData.step1.carrierName}
        vehicleInfo={`${formData.step3.vehicleYear} ${formData.step3.vehicleMake} ${formData.step3.vehicleModel}`}
        userName={formData.step2.fullLegalName}
        submissionId={lastSubmissionId ?? undefined}
        emailStatus={lastEmailStatus}
      />
    );
  }

  if (intakePhase === "choosing") {
    return (
      <LandingChoice
        onChooseUpload={() => {
          trackEvent("upload_path_chosen", {});
          setIntakePhase("uploading");
        }}
        onChooseManual={() => {
          trackEvent("manual_path_chosen", {});
          setIntakePhase("form");
        }}
      />
    );
  }

  if (intakePhase === "uploading") {
    return (
      <DecPageUploader
        onExtracted={(result) => {
          setExtraction(result);
          trackEvent("extraction_completed", {
            documentType: result.extracted.documentType,
            overallConfidence: result.extracted.overallConfidence,
            durationMs: result.durationMs,
          });
          setIntakePhase("reviewing");
        }}
        onFallbackToManual={() => {
          trackEvent("upload_bail_out", {});
          setIntakePhase("form");
        }}
      />
    );
  }

  if (intakePhase === "reviewing" && extraction) {
    return (
      <ExtractionReview
        extracted={extraction.extracted}
        onContinue={(mappedForm: MappedFormData, metadata: FieldMetadata) => {
          applyMappedFormToRHF(methods.setValue, mappedForm);
          setExtractionMetadata(metadata);
          trackEvent("review_screen_viewed", {
            total_fields: Object.keys(metadata).length,
            low_confidence_count: Object.values(metadata).filter(
              (m) => m.confidence === "low",
            ).length,
          });
          setIntakePhase("form");
        }}
        onStartOver={() => {
          setExtraction(null);
          setExtractionMetadata({});
          setIntakePhase("form");
        }}
      />
    );
  }

  return (
    <ExtractionMetadataContext.Provider
      value={{
        metadata: extractionMetadata,
        setMetadata: (updater) => setExtractionMetadata(updater),
      }}
    >
      <FormProvider {...methods}>
        <FormLayout
        currentStep={currentStep}
        completedSteps={completedSteps}
        onBack={handleBack}
        onNext={currentStep === TOTAL_STEPS ? handleSubmit : handleNext}
        onStepClick={handleStepClick}
        isFirstStep={currentStep === 1}
        isLastStep={currentStep === TOTAL_STEPS}
        lastSaved={lastSaved}
        isSaving={isSaving || isSubmitting}
      >
        {submitError && (
          <div className="mb-6 rounded-lg p-4 text-sm" style={{ border: "1px solid #fecaca", background: "#fef2f2", color: "var(--color-error)" }}>
            {submitError}
          </div>
        )}
        {renderStep()}
      </FormLayout>

      {/* Session timeout warning modal */}
      {showTimeoutWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md bg-white p-6 shadow-lg" style={{ borderRadius: 16 }}>
            <h2 className="mb-2 text-lg font-semibold" style={{ color: "var(--color-navy)" }}>
              Still there?
            </h2>
            <p className="mb-4" style={{ color: "var(--text-secondary)" }}>
              For your security, this form will clear in 3 minutes due to
              inactivity.
            </p>
            <button
              onClick={() => {/* any interaction resets the timer via event listeners */}}
              className="btn-primary w-full"
            >
              I'm still here
            </button>
          </div>
        </div>
      )}
      </FormProvider>
    </ExtractionMetadataContext.Provider>
  );
}

/**
 * Apply extracted (mapped) form data to react-hook-form state. Walks each
 * step slice of MappedFormData and sets values with shouldValidate=false
 * (so the user isn't greeted by red error states on pre-filled fields).
 */
function applyMappedFormToRHF(
  setValue: UseFormSetValue<FullFormData>,
  mapped: MappedFormData,
): void {
  const apply = (stepKey: keyof MappedFormData, data: Record<string, unknown>) => {
    for (const [fieldKey, fieldValue] of Object.entries(data)) {
      if (fieldValue === undefined) continue;
      setValue(`${stepKey}.${fieldKey}` as FieldPath<FullFormData>, fieldValue as never, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  };
  if (mapped.step1) apply("step1", mapped.step1 as Record<string, unknown>);
  if (mapped.step2) apply("step2", mapped.step2 as Record<string, unknown>);
  if (mapped.step3) apply("step3", mapped.step3 as Record<string, unknown>);
  if (mapped.step5) apply("step5", mapped.step5 as Record<string, unknown>);
  if (mapped.step6) apply("step6", mapped.step6 as Record<string, unknown>);
}

/**
 * AuthGate - Simple sign-in / sign-up gate.
 * Users must authenticate before they can access the intake form.
 * Supports email + password and magic link (Supabase Auth).
 */
function AuthGate({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const siteUrl =
    (import.meta.env.VITE_SITE_URL as string | undefined) ??
    (typeof window !== "undefined" ? window.location.origin : "");
  const authRedirectTo = `${siteUrl}/`;

  const handleEmailPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsLoading(true);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: authRedirectTo },
        });
        if (error) throw error;
        // Supabase may require email confirmation depending on settings
        onAuthenticated();
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuthenticated();
      }
    } catch (err: any) {
      setAuthError(err.message || "Authentication failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: authRedirectTo },
      });
      if (error) throw error;
      setMagicLinkSent(true);
    } catch (err: any) {
      setAuthError(err.message || "Failed to send magic link.");
    } finally {
      setIsLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <div className="mx-auto max-w-md px-4 py-16 text-center">
        <div className="step-card">
          <h2 className="mb-2 text-xl font-bold text-brand-primary">Check your email</h2>
          <p className="text-gray-600">
            We sent a sign-in link to <strong>{email}</strong>. Click the link to continue.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="step-card">
        <h2 className="mb-2 text-xl font-bold text-brand-primary">
          {isSignUp ? "Create your account" : "Sign in to continue"}
        </h2>
        <p className="mb-6 text-sm text-gray-500">
          {isSignUp
            ? "Create an account so you can save your progress and come back later."
            : "Sign in to start or resume your intake form."}
        </p>

        {authError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {authError}
          </div>
        )}

        <form onSubmit={useMagicLink ? handleMagicLink : handleEmailPassword}>
          <div className="mb-4">
            <label htmlFor="auth-email" className="form-label">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input mt-1"
              placeholder="you@example.com"
              required
            />
          </div>

          {!useMagicLink && (
            <div className="mb-4">
              <label htmlFor="auth-password" className="form-label">Password</label>
              <input
                id="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input mt-1"
                placeholder="At least 6 characters"
                minLength={6}
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full"
          >
            {isLoading
              ? "Please wait..."
              : useMagicLink
              ? "Send magic link"
              : isSignUp
              ? "Create account"
              : "Sign in"}
          </button>
        </form>

        <div className="mt-4 space-y-2 text-center text-sm text-gray-500">
          <button
            type="button"
            onClick={() => { setUseMagicLink(!useMagicLink); setAuthError(null); }}
            className="text-brand-primary hover:underline"
          >
            {useMagicLink ? "Use email + password instead" : "Sign in with a magic link instead"}
          </button>
          <br />
          {!useMagicLink && (
            <button
              type="button"
              onClick={() => { setIsSignUp(!isSignUp); setAuthError(null); }}
              className="text-brand-primary hover:underline"
            >
              {isSignUp ? "Already have an account? Sign in" : "Don't have an account? Sign up"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * App root with providers.
 * Handles routing between the intake form and admin view.
 * Enforces authentication before the form is accessible.
 */
// Coming soon gate: add ?preview=1 to URL to bypass
function ComingSoon() {
  return (
    <div style={{
      fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      background: "#111111",
      color: "white",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      padding: "40px 24px",
    }}>
      <div style={{ maxWidth: 480 }}>
        <img src="/logo-white.svg" alt="OneCalla" style={{ height: 32, marginBottom: 48, marginLeft: "auto", marginRight: "auto" }} />
        <h1 style={{ fontSize: "clamp(36px, 6vw, 56px)", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.1 }}>Coming soon.</h1>
      </div>
    </div>
  );
}

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null); // null = loading

  // Coming soon gate
  const params = new URLSearchParams(window.location.search);
  if (params.get("early") !== "access") {
    return <ComingSoon />;
  }

  useEffect(() => {
    // Initialize PostHog analytics
    initAnalytics();

    // Check current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
      if (session) {
        identifyUser(session.user.id, { email: session.user.email });
      }
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setIsLoggedIn(!!session);
        if (session) {
          identifyUser(session.user.id, { email: session.user.email });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Simple route check for /admin and /claim/:code
  const isAdminRoute = window.location.pathname === "/admin";
  const isClaimRoute = /^\/claim\/[A-Za-z0-9_-]{4,40}$/.test(window.location.pathname);

  if (isAdminRoute) {
    return <AdminView />;
  }
  if (isClaimRoute) {
    return <ClaimPage />;
  }

  // Show loading while checking auth
  if (isLoggedIn === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "var(--bg-page)" }}>
        <p style={{ color: "var(--text-muted)" }}>Loading...</p>
      </div>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <div className="min-h-screen py-8" style={{ backgroundColor: "var(--bg-page)" }}>
        <header className="mx-auto mb-8 max-w-form px-4 text-center">
          <img src="/logo-green.svg" alt="OneCalla" style={{ height: 36 }} className="mx-auto" />
          <p className="mt-1" style={{ color: "var(--text-muted)" }}>
            Pay less on the auto insurance you already have.
          </p>
        </header>

        {isLoggedIn ? (
          <IntakeForm />
        ) : (
          <AuthGate onAuthenticated={() => setIsLoggedIn(true)} />
        )}
      </div>
      <Analytics />
      <SpeedInsights />
    </GoogleReCaptchaProvider>
  );
}
